import React, { useEffect, useState } from 'react';

export default function AdminPanel({ apiUrl, token, onClose }) {
  const [users, setUsers] = useState([]);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const bearer = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchUsers = async () => {
    const res = await fetch(`${apiUrl}/admin/users`, { headers: bearer });
    if (res.ok) setUsers(await res.json());
  };

  useEffect(() => { fetchUsers(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const res = await fetch(`${apiUrl}/admin/create-user`, {
        method: 'POST',
        headers: bearer,
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error); return; }
      setSuccess(`User "${data.user.username}" created!`);
      setUsername('');
      setPassword('');
      fetchUsers();
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Delete user "${name}"?`)) return;
    await fetch(`${apiUrl}/admin/users/${id}`, { method: 'DELETE', headers: bearer });
    fetchUsers();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="w-full max-w-md rounded-2xl bg-slate-800 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-700 px-6 py-4">
          <h2 className="text-lg font-semibold">Admin — Manage Users</h2>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-700 hover:text-white">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4 space-y-5">
          {/* Create user form */}
          <form onSubmit={createUser} className="space-y-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-400">Create New User</h3>

            {error && <p className="rounded-lg border border-red-500/30 bg-red-500/20 px-3 py-2 text-sm text-red-300">{error}</p>}
            {success && <p className="rounded-lg border border-emerald-500/30 bg-emerald-500/20 px-3 py-2 text-sm text-emerald-300">{success}</p>}

            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              required
              minLength={3}
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none"
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password (min 8 chars)"
              required
              minLength={8}
              className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-indigo-600 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-60"
            >
              {loading ? 'Creating…' : 'Create User'}
            </button>
          </form>

          {/* User list */}
          <div>
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-400">All Users</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {users.map((u) => (
                <div key={u.id} className="flex items-center justify-between rounded-lg bg-slate-700/50 px-3 py-2">
                  <div>
                    <span className="text-sm font-medium">{u.username}</span>
                    {u.is_admin && <span className="ml-2 rounded bg-indigo-600/50 px-1.5 py-0.5 text-xs">admin</span>}
                  </div>
                  {!u.is_admin && (
                    <button
                      onClick={() => deleteUser(u.id, u.username)}
                      className="rounded p-1 text-slate-400 hover:bg-red-500/20 hover:text-red-400"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
